import { localizedPages } from "./localization.config";

/**
 * @typedef {Object} PageDescriptor
 * @property {string} name - Unique identifier for the page.
 * @property {Object} localizedPages - Localized page definitions for different languages.
 * @property {Object} sitemapConfig - Sitemap configuration for SEO.
 * @property {string} sitemapConfig.changefreq - How often the page content changes (e.g., "weekly", "monthly").
 * @property {number} sitemapConfig.priority - Priority of the page in the sitemap (0.0 to 1.0).
 * @property {function} sitemapConfig.noIndexDecorator - Optional function to determine if the page should be no-indexed based on locale or other conditions.
 * @property {boolean|Object} sitemapConfig.noIndex - Optional global or locale-specific no-index setting.
 */

export const pageDescriptors = {
  professionals: {
    name: "professionals",
    localizedPages: {
      ...localizedPages["professionals/_profession/_city/index"],
    },
    sitemapConfig: {
      changefreq: "weekly",
      priority: 0.9,
      noIndexDecorator: ({ _profession, city }) => {
        if (!Object.hasOwn(city, "population")) {
          throw new Error(
            "City object must have a population property for noIndexDecorator."
          );
        }
        return city.population <= 3000;
      },
    },
  },
  professionPage: {
    name: "professionPage",
    localizedPages: {
      ...localizedPages["professionals/_profession/index"],
    },
    sitemapConfig: {
      changefreq: "weekly",
      priority: 0.9,
    },
  },
  professionalPage: {
    name: "professionalPage",
    localizedPages: {
      ...localizedPages["professional/_professionalSeoName/index"],
    },
    sitemapConfig: {
      changefreq: "weekly",
      priority: 0.9,
    },
  },
  pricesPage: {
    name: "pricesPage",
    localizedPages: { ...localizedPages["prices/index"] },
    sitemapConfig: {
      changefreq: "weekly",
      priority: 0.9,
    },
  },
  prices: {
    name: "prices",
    localizedPages: { ...localizedPages["prices/_professionTask"] },
    sitemapConfig: {
      changefreq: "weekly",
      priority: 0.9,
    },
  },
  questionsPage: {
    name: "questionsPage",
    localizedPages: { ...localizedPages["questions/index"] },
    sitemapConfig: {
      changefreq: "weekly",
      priority: 0.9,
    },
  },
  questionPage: {
    name: "questionPage",
    localizedPages: { ...localizedPages["questions/_questionSeoName/index"] },
    sitemapConfig: {
      changefreq: "weekly",
      priority: 0.9,
    },
  },
  professionQuestions: {
    name: "professionQuestions",
    localizedPages: {
      ...localizedPages["questions/profession/_professionSeoName/index"],
    },
    sitemapConfig: {
      changefreq: "weekly",
      priority: 0.9,
    },
  },
  professionTaskQuestions: {
    name: "professionTaskQuestions",
    localizedPages: {
      ...localizedPages[
        "questions/profession-task/_professionTaskSeoName/index"
      ],
    },
    sitemapConfig: {
      changefreq: "weekly",
      priority: 0.9,
    },
  },
  inspirationLandingPage: {
    name: "inspirationLandingPage",
    localizedPages: { ...localizedPages["inspiration/index"] },
    sitemapConfig: {
      changefreq: "weekly",
      priority: 0.9,
    },
  },
  inspirationPage: {
    name: "inspirationPage",
    localizedPages: {
      ...localizedPages["inspiration/_inspirationSeoName/index"],
    },
    sitemapConfig: {
      changefreq: "weekly",
      priority: 0.9,
      noIndex: {
        de: true,
      },
    },
  },
  inspirationPicturePage: {
    name: "inspirationPicturePage",
    localizedPages: {
      ...localizedPages[
        "inspiration/_inspirationSeoName/_pictureSeoName/index"
      ],
    },
    sitemapConfig: {
      changefreq: "monthly",
      priority: 0.9,
      noIndex: {
        de: true,
      },
    },
  },
  blogLandingPage: {
    name: "blogLandingPage",
    localizedPages: { ...localizedPages["blog/index"] },
    sitemapConfig: {
      changefreq: "weekly",
      priority: 0.9,
    },
  },
  blogPage: {
    name: "blogPage",
    localizedPages: {
      ...localizedPages["blog/_articleSeoName"],
    },
    sitemapConfig: {
      changefreq: "weekly",
      priority: 0.9,
    },
  },
};
